import { DefaultTheme, ThemedStyledProps } from 'styled-components';
import { ColorVariants, Palette } from 'types';
import { ShorthandActionColor } from './types';

const SHORTHAND_COLOR_PALETTE_MAP = {
  primary: 'actionPrimary',
  secondary: 'actionSecondary',
  tertiary: 'actionTertiary',
} as const;

type ColorsOfType<T> = {
  [Color in keyof Palette]: Palette[Color] extends T ? Color : never;
}[keyof Palette];

type SimpleColor = ColorsOfType<string>;
type CommonColor = keyof Palette['common'];
type VariantColor = ColorsOfType<ColorVariants>;

interface GetThemeColor {
  (colorKey: SimpleColor): (
    props: ThemedStyledProps<unknown, DefaultTheme>,
  ) => string;
  (colorKey: CommonColor): (
    props: ThemedStyledProps<unknown, DefaultTheme>,
  ) => string;
  (colorKey: VariantColor, variant?: keyof ColorVariants): (
    props: ThemedStyledProps<unknown, DefaultTheme>,
  ) => string;
  (colorKey: SimpleColor | CommonColor | VariantColor): (
    props: ThemedStyledProps<unknown, DefaultTheme>,
  ) => string;
}

type GetShorthandActionColor = (
  colorKey: ShorthandActionColor,
  variant?: keyof ColorVariants,
) => (props: ThemedStyledProps<unknown, DefaultTheme>) => string;

function isCommonColorKey(key: string): key is CommonColor {
  return key === 'white' || key === 'black';
}

const getThemeColor: GetThemeColor =
  (colorKey, variant: keyof ColorVariants = 'main') =>
  (props) => {
    if (isCommonColorKey(colorKey)) {
      return props.theme.palette.common[colorKey];
    }

    const color = props.theme.palette[colorKey];

    if (typeof color === 'string') {
      return color;
    }

    return color[variant];
  };

export const getShorthandActionColor: GetShorthandActionColor =
  (colorKey, variant = 'main') =>
  (props) => {
    if (colorKey === 'danger') {
      return props.theme.palette.danger;
    }

    const actionPaletteKey = SHORTHAND_COLOR_PALETTE_MAP[colorKey];
    return props.theme.palette[actionPaletteKey][variant];
  };

export default getThemeColor;
